import dayjs from "dayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween";
import isTodayPlugin from "dayjs/plugin/isToday";
import timezonePlugin from "dayjs/plugin/timezone";
import utcPlugin from "dayjs/plugin/utc";
import { DaySecond } from "./DaySecond";
import { makeImmutable } from "./makeImmutable";
dayjs.extend(isBetweenPlugin);
dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);
dayjs.extend(isTodayPlugin);
export const WEEKDAY_NAMES = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
export const ISO_WEEKDAY_NAMES = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];
/*
 * Restaumatic date wrapper. Represents Date in the restaurant timezone
 */
export class RDate {
    constructor(raw, tz) {
        this.raw = raw;
        this.tz = tz;
        makeImmutable(this);
    }
    static now(tz) {
        return new RDate(dayjs.utc().tz(tz), tz);
    }
    static fromString(str, timezone) {
        return new RDate(dayjs.utc(str).tz(timezone), timezone);
    }
    static fromLocalTimeString(strInLocalTime, timezone) {
        return new RDate(dayjs.tz(strInLocalTime, timezone), timezone);
    }
    static fromHourAndMinuteString(str, timezone) {
        const [hour, minute] = str.split(":");
        return new RDate(dayjs
            .utc()
            .tz(timezone)
            .set("hours", parseInt(hour))
            .set("minute", parseInt(minute))
            .set("second", 0), timezone);
    }
    static fromDateAndTime({ raw: date, tz: timezone1 }, { raw: time, tz: timezone2 }) {
        if (timezone1 !== timezone2)
            throw new Error("Passed arguments in different timezones");
        const raw = date
            .set("hour", time.hour())
            .set("minute", time.minute())
            .set("second", time.second());
        return new RDate(raw, timezone1);
    }
    get toString() {
        return this.raw.toISOString();
    }
    get weekday() {
        return WEEKDAY_NAMES[this.raw.day()];
    }
    get secondOfDay() {
        return DaySecond.fromNumber(this.raw.diff(this.raw.startOf("day"), "second"));
    }
    get isValid() {
        return this.raw.isValid();
    }
    /**
     * Format as "18:34"
     */
    get fullHour() {
        return this.raw.format("H:mm");
    }
    /**
     * Format as "YYYY-MM-DD"
     */
    get fullDate() {
        return this.raw.format("YYYY-MM-DD");
    }
    /**
     * Format with provided template
     */
    format(template) {
        return this.raw.format(template);
    }
    /**
     * Check if this date is before the provided date
     */
    isBefore(other) {
        return this.raw.isBefore(other.raw);
    }
    /**
     * Check if this date is after the provided date
     */
    isAfter(other) {
        return this.raw.isAfter(other.raw);
    }
    eq(other) {
        return this.raw.isSame(other.raw);
    }
    /**
     * Check if this date is between the provided dates
     */
    isBetween(begin, end) {
        return (this.raw.isSame(begin.raw) ||
            this.raw.isBetween(begin.raw, end.raw) ||
            this.raw.isSame(end.raw));
    }
    isSame(other, unit) {
        return this.raw.isSame(other.raw, unit);
    }
    get isToday() {
        return this.raw.isToday();
    }
    diff(other, unit) {
        return this.raw.diff(other.raw, unit);
    }
    add(number, unit) {
        return new RDate(this.raw.add(number, unit), this.tz);
    }
    startOfDay() {
        return new RDate(this.raw.startOf("day"), this.tz);
    }
    endOfDay() {
        return new RDate(this.raw.endOf("day"), this.tz);
    }
}
